import { Drawer } from '@base-ui/react/drawer';
import { useRender } from '@base-ui/react/use-render';
import { cn } from '@foundation/utils/cn';
import {
  createContext,
  ReactElement,
  ReactNode,
  useCallback,
  useContext,
  useId,
  useMemo,
  useState,
} from 'react';

import {
  HeadlessDrawer,
  HeadlessDrawerPresentation,
  HeadlessDrawerStyles,
} from './Drawer';
import {
  resolveResponsiveProp,
  type Responsive,
} from '../primitives/responsive';
import { useMediaQuery } from '../primitives/useMediaQuery';

export type AppFramePanelWidth = 'small' | 'medium' | 'large';

/**
 * Px equivalents of the width tokens, used to decide whether pushing still
 * leaves enough content width. The ui layer spells the same values out as
 * literal Tailwind classes - Tailwind cannot read them from here, so the two
 * have to be kept in sync.
 */
export const APP_FRAME_PANEL_WIDTH_PX = {
  small: 320,
  medium: 400,
  large: 560,
} as const satisfies Record<AppFramePanelWidth, number>;

export const APP_FRAME_DEFAULTS = {
  width: 'medium',
  panelOffsetTop: 0,
} as const;

export type AppFrameContextValue = {
  readonly isOpen: boolean;
  readonly open: () => void;
  readonly close: () => void;
  readonly toggle: () => void;
  /** Whether the panel currently pushes content (`side`) or covers it (`sheet`). */
  readonly presentation: HeadlessDrawerPresentation;
  /** Id of the panel element, for `aria-controls`. */
  readonly panelId: string;
  readonly panelOffsetTop: number;
  readonly width: Responsive<AppFramePanelWidth>;
};

const AppFrameContext = createContext<AppFrameContextValue | null>(null);

export function useAppFrame(): AppFrameContextValue {
  const context = useContext(AppFrameContext);
  if (!context) {
    throw new Error('useAppFrame must be used within <AppFrame.Root>');
  }
  return context;
}

type UseAppFramePresentationOptions = {
  readonly minContentWidth: number;
  readonly width?: Responsive<AppFramePanelWidth>;
};

/**
 * Resolves the panel presentation: pushing is only viable while the content
 * keeps at least `minContentWidth`. Uses the *widest* configured panel width,
 * since a responsive `width` has to clear the threshold at every breakpoint.
 */
function useAppFramePresentation({
  minContentWidth,
  width,
}: UseAppFramePresentationOptions): HeadlessDrawerPresentation {
  const widths = Object.values(
    resolveResponsiveProp(width, APP_FRAME_DEFAULTS.width),
  ).map((token) => APP_FRAME_PANEL_WIDTH_PX[token]);

  const threshold = Math.max(...widths) + minContentWidth;
  return useMediaQuery(`(min-width: ${threshold}px)`) ? 'side' : 'sheet';
}

export type HeadlessAppFrameRootProps = {
  readonly children: ReactNode;
  /**
   * Narrowest `children` may be squeezed to, in px. Below
   * `panelWidth + minContentWidth` the panel covers the content instead of
   * pushing it. Required: only the app knows what its own layout needs.
   */
  readonly minContentWidth: number;
  /** Panel width. Lives here because the presentation threshold depends on it. */
  readonly width?: Responsive<AppFramePanelWidth>;
  /** Distance the sheet is offset from the top, to clear a fixed app header. */
  readonly panelOffsetTop?: number;
  /** Controlled open state. Omit to let the frame manage it. */
  readonly open?: boolean;
  readonly onOpenChange?: (open: boolean) => void;
  readonly id?: string;
  readonly className?: string;
  readonly 'data-testid'?: string;
};

function Root({
  children,
  minContentWidth,
  width = APP_FRAME_DEFAULTS.width,
  panelOffsetTop = APP_FRAME_DEFAULTS.panelOffsetTop,
  open: controlledOpen,
  onOpenChange,
  id,
  className,
  'data-testid': dataTestId,
}: HeadlessAppFrameRootProps) {
  const generatedId = useId();
  const panelId = id ?? `app-frame-panel-${generatedId}`;

  // Uncontrolled unless `open` is supplied; the panel always starts closed.
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const isControlled = controlledOpen !== undefined;
  const isOpen = isControlled ? controlledOpen : uncontrolledOpen;

  const setOpen = useCallback(
    (next: boolean) => {
      if (!isControlled) setUncontrolledOpen(next);
      onOpenChange?.(next);
    },
    [isControlled, onOpenChange],
  );

  const presentation = useAppFramePresentation({ minContentWidth, width });

  const context = useMemo<AppFrameContextValue>(
    () => ({
      isOpen,
      open: () => setOpen(true),
      close: () => setOpen(false),
      toggle: () => setOpen(!isOpen),
      presentation,
      panelId,
      panelOffsetTop,
      width,
    }),
    [isOpen, setOpen, presentation, panelId, panelOffsetTop, width],
  );

  return (
    <AppFrameContext.Provider value={context}>
      <Drawer.Provider>
        <div
          className={cn('relative h-dvh overflow-hidden', className)}
          data-testid={dataTestId}
        >
          {children}
        </div>
      </Drawer.Provider>
    </AppFrameContext.Provider>
  );
}

export type HeadlessAppFrameContentProps = {
  readonly children: ReactNode;
  readonly className?: string;
  readonly 'data-testid'?: string;
};

/** The app's own UI. Gets pushed by the panel in `side` presentation. */
function Content({
  children,
  className,
  'data-testid': dataTestId,
}: HeadlessAppFrameContentProps) {
  return (
    <Drawer.Indent
      className={cn('h-full min-w-0 overflow-y-auto', className)}
      data-app-frame-content=""
      data-testid={dataTestId}
    >
      {/* Shadows the outer provider so the app's own drawers and modals,
          which all report to the nearest one, never push the layout. */}
      <Drawer.Provider>{children}</Drawer.Provider>
    </Drawer.Indent>
  );
}

export type HeadlessAppFramePanelProps = {
  readonly children: ReactNode;
  /** An accessible name supplied to the Base UI drawer popup */
  readonly label: string;
  readonly styles?: HeadlessDrawerStyles;
  readonly 'data-testid'?: string;
};

function Panel({
  children,
  label,
  styles,
  'data-testid': dataTestId,
}: HeadlessAppFramePanelProps) {
  const { isOpen, close, presentation, panelId, panelOffsetTop } =
    useAppFrame();
  const isSide = presentation === 'side';

  return (
    <HeadlessDrawer.Root
      isOpen={isOpen}
      onClose={close}
      position="right"
      presentation={presentation}
      // Non-modal in both presentations: pushed content stays interactive on
      // desktop, and the app header stays reachable above the sheet on mobile.
      modal={false}
      dismiss={{
        outsidePress: false,
        escapeKey: false,
        focusOut: false,
        swipe: !isSide,
      }}
      showBackdrop={false}
      showCloseButton={false}
      virtualKeyboard
      containerStyle={
        isSide
          ? undefined
          : {
              top: `max(${panelOffsetTop}px, env(safe-area-inset-top))`,
            }
      }
      id={panelId}
      aria-label={label}
      styles={styles}
      data-testid={dataTestId}
    >
      {children}
    </HeadlessDrawer.Root>
  );
}

export type HeadlessAppFramePanelHeaderProps = {
  readonly children?: ReactNode;
  readonly closeButton: ReactElement;
  readonly className?: string;
  readonly 'data-testid'?: string;
};

function PanelHeader({
  children,
  closeButton,
  className,
  'data-testid': dataTestId,
}: HeadlessAppFramePanelHeaderProps) {
  const { presentation } = useAppFrame();

  return (
    <div
      className={cn(
        'flex shrink-0 items-center justify-between [&>button]:shrink-0',
        className,
        presentation === 'side' &&
          'pt-[calc(env(safe-area-inset-top)+var(--app-frame-header-padding-top,0px))]',
      )}
      data-testid={dataTestId}
    >
      <div className="min-w-0 flex-1">{children}</div>
      <HeadlessDrawer.Close render={closeButton} />
    </div>
  );
}

export type HeadlessAppFramePanelBodyProps = {
  readonly children: ReactNode;
  readonly className?: string;
  readonly 'data-testid'?: string;
};

function PanelBody({
  children,
  className,
  'data-testid': dataTestId,
}: HeadlessAppFramePanelBodyProps) {
  return (
    <div
      className={cn(
        'flex min-h-0 flex-1 flex-col',
        'pb-[max(env(safe-area-inset-bottom),var(--drawer-keyboard-inset,0px))]',
        className,
      )}
      data-testid={dataTestId}
    >
      {children}
    </div>
  );
}

export type HeadlessAppFrameTriggerProps = {
  /** The visible control - a button, a link, anything that can take an onClick. */
  readonly children: useRender.RenderProp;
};

/**
 * Renders no element of its own: it clones `children` and wires the toggle plus
 * the `aria-controls`/`aria-expanded` pair that would otherwise be hand-written.
 */
function Trigger({ children }: HeadlessAppFrameTriggerProps) {
  const { isOpen, toggle, panelId } = useAppFrame();

  return useRender({
    render: children,
    props: {
      onClick: toggle,
      'aria-controls': panelId,
      'aria-expanded': isOpen,
    },
  });
}

export const HeadlessAppFrame = {
  Root,
  Content,
  Panel,
  PanelHeader,
  PanelBody,
  Trigger,
};
